// src/components/viewer/ViewerFreeTextSearch.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  Group,
  TextInput,
  ActionIcon,
  Tooltip,
  SegmentedControl,
  Autocomplete,
} from "@mantine/core";
import { FaSearch } from "react-icons/fa";
import { FiX, FiInfo } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearFreeTextSearchInRedux,
  selectActiveSearch,
  selectComponentSearchText,
  selectRoomSearchText,
  setActiveSearchInRedux,
  setComponentSearchTextInRedux,
  setRoomSearchTextInRedux,
} from "../../ViewerRedux/viewerSlice";
import { MEDIA_ASSET_KEYS } from "@multiconsult-group/multiplacer-core";
import { MediaAssetViewer } from "../../MediaAssets/MediaAssetViewer";
import { selectComponentAutocompleteValues } from "../../ViewerRedux/viewerSelectors";

export interface ViewerFreeTextSearchProps {
  width?: number;
  disabled?: boolean;
  debounceMs?: number;
}

export const ViewerFreeTextSearch: React.FC<ViewerFreeTextSearchProps> = ({
  width = 280,
  disabled = false,
  debounceMs = 150,
}) => {
  const dispatch = useAppDispatch();

  const activeSearch = useAppSelector(selectActiveSearch);
  const roomText = useAppSelector(selectRoomSearchText);
  const componentText = useAppSelector(selectComponentSearchText);
  const componentValues = useAppSelector(selectComponentAutocompleteValues);

  const isComponentMode = activeSearch === "COMPONENT";
  const reduxValue = isComponentMode ? componentText : roomText;

  const [localValue, setLocalValue] = useState<string>(reduxValue);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setLocalValue(reduxValue);
  }, [reduxValue, activeSearch]);

  const dispatchValueDebounced = (next: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);

    timerRef.current = window.setTimeout(() => {
      if (isComponentMode) dispatch(setComponentSearchTextInRedux(next));
      else dispatch(setRoomSearchTextInRedux(next));
    }, debounceMs);
  };

  const onChangeValue = (next: string) => {
    setLocalValue(next);
    dispatchValueDebounced(next);
  };

  const handleClear = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setLocalValue("");
    dispatch(clearFreeTextSearchInRedux());
  };

  const helpText = isComponentMode
    ? "Søk på: navn, compCode, typeName eller system."
    : "Søk på romnummer eller romnavn.";

  const rightSection = (
    <Group gap={2} wrap="nowrap">
      <Tooltip label={helpText} withArrow>
        <ActionIcon variant="subtle" size="sm" radius="xl">
          <FiInfo size={14} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Tøm søk" withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="xl"
          disabled={!localValue.trim()}
          onClick={handleClear}
        >
          <FiX size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  return (
    <Group gap="xs" align="center" wrap="nowrap">
      <MediaAssetViewer assetKey={MEDIA_ASSET_KEYS.FREETEXT_SEARCH} mode="ICON" />

      <SegmentedControl
        size="sm"
        value={activeSearch}
        disabled={disabled}
        color="blue"
        onChange={(v) => dispatch(setActiveSearchInRedux(v as "ROOM" | "COMPONENT"))}
        data={[
          { label: "Rom", value: "ROOM" },
          { label: "Komponent", value: "COMPONENT" },
        ]}
      />

      {isComponentMode ? (
        <Autocomplete
          style={{ width }}
          size="sm"
          placeholder="Søk komponent"
          value={localValue}
          onChange={onChangeValue}
          data={componentValues}
          limit={25}
          leftSection={<FaSearch size={12} />}
          rightSectionWidth={72}
          rightSection={rightSection}
          onOptionSubmit={(val) => {
            setLocalValue(val);
            dispatchValueDebounced(val);
          }}
        />
      ) : (
        <TextInput
          style={{ width }}
          size="sm"
          placeholder="Søk rom"
          value={localValue}
          onChange={(e) => onChangeValue(e.currentTarget.value)}
          leftSection={<FaSearch size={12} />}
          rightSectionWidth={72}
          rightSection={rightSection}
        />
      )}
    </Group>
  );
};
